'use client'

import { z } from 'zod'

import {
  serverImportAnalyzeSchema,
  serverImportConfirmSchema,
  serverImportOperationSchema,
} from '@/features/studio-renew/schemas/server-import.schema'
import { studioSession } from '@/features/studio-renew/services/studio-session'
import {
  ApiError,
  runOperation,
  waitOperation,
} from '@/features/veluvit/services/api'

import type {
  ServerImportAnalyze,
  ServerImportConfirm,
} from '@/features/studio-renew/schemas/server-import.schema'
import type { OperationOptions } from '@/features/veluvit/services/api'

const resultSchema = z.record(z.string(), z.unknown())

function watch(requestId: string, options: OperationOptions) {
  const session = studioSession()
  let observed = false
  return {
    options: {
      ...options,
      onOperation(operation) {
        session.assertCurrent()
        const parsed = serverImportOperationSchema.safeParse(operation)
        if (!parsed.success || parsed.data.requestId !== requestId)
          throw new ApiError(
            '이식 작업 응답이 요청과 일치하지 않아요. 기존 작업을 먼저 확인해 주세요.',
            502,
            'IMPORT_OPERATION_MISMATCH'
          )
        observed = true
        options.onOperation?.(operation)
      },
    } satisfies OperationOptions,
    finish(value: unknown) {
      session.assertCurrent()
      if (!observed)
        throw new ApiError(
          '이식 작업 정보를 확인하지 못했어요.',
          502,
          'INVALID_IMPORT_OPERATION'
        )
      return resultSchema.parse(value)
    },
  }
}

/** 호출자가 보관한 같은 requestId를 사용한다. 오류 뒤 새 ID나 로컬 생성으로 자동 대체하지 않는다. */
export async function analyzeServerImport(
  input: ServerImportAnalyze,
  options: OperationOptions = {}
) {
  const payload = serverImportAnalyzeSchema.parse(input)
  const operation = watch(payload.requestId, options)
  return operation.finish(
    await runOperation<unknown>(
      '/creator/me/import/analyze/',
      payload,
      operation.options
    )
  )
}

/** 분석에서 검토한 선택만 한 번에 확정한다. 과거 다중 CRUD/되돌리기와 섞지 않는다. */
export async function confirmServerImport(
  input: ServerImportConfirm,
  options: OperationOptions = {}
) {
  const payload = serverImportConfirmSchema.parse(input)
  const operation = watch(payload.requestId, options)
  return operation.finish(
    await runOperation<unknown>(
      '/creator/me/import/confirm/',
      payload,
      operation.options
    )
  )
}

/** 새로고침·응답 유실 복구는 실제 Operation ID로 GET만 수행한다. */
export async function resumeServerImport(
  operationId: string,
  requestId: string,
  options: OperationOptions = {}
) {
  z.string().min(1).parse(operationId)
  z.string()
    .regex(/^[\w-]{8,100}$/)
    .parse(requestId)
  const operation = watch(requestId, options)
  return operation.finish(
    await waitOperation<unknown>(operationId, operation.options)
  )
}
